// START IMPORTS
// Refresh lib imports
import googleTagManager, {
  GoogleTagManagerConfig,
} from '@analytics/google-tag-manager';
import { initialize } from '@ltv-apm-modules/react';
import { version, CookiesStorage } from '@ltvco/refresh-lib';
import { SubscriptionAccessConfigProvider } from '@ltvco/refresh-lib/ctx';
import {
  FamilyAndAncestryDashboard,
  FamilyAlbum,
  AIFamilyPhotosPage,
  FamilyInsightsPage,
  FamilyMapPage,
} from '@ltvco/refresh-lib/fmt';
import { TosModalLegalError } from '@ltvco/refresh-lib/payments';
import { ErrorBoundary, styled } from '@ltvco/refresh-lib/theme';
import { ErrorRequestData, ResponseError } from '@ltvco/refresh-lib/utils';
import {
  CancellationReasonsModal,
  CancelUserExperienceModal,
  closeSnackbar,
  ConfirmCancelModal,
  ContactOptionsModal,
  ForgotLoginModal,
  invalidateSession,
  isUserLoggedIn,
  MagicLinkModal,
  ResetPasswordRequestForm,
  SearchData,
  SessionProvider,
  TrustPilotModal,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  SearchFormProvider,
  Footer,
  BannerMigrationCta,
  ComplianceRecomplyPage,
  ComplianceFormPage,
  CompliancePermittedPage,
  ComplianceProhibitedPage,
  SelfSearchingContainer,
  NonCompliancePage,
  ObituarySearchPage,
  EmailSearchPage,
} from '@ltvco/refresh-lib/v2';
import Analytics from 'analytics';
import { useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  Navigate,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

import 'App.css';
import { constants } from 'appConstants';
import { AppContainer } from 'components/AppContainer';
import { BaseNumerologyQueryKeys } from 'components/Astrology/interfaces';
import { ChatBVContainer } from 'components/ChatBV/components';
import { EmailVerification } from 'components/EmailVerification';
import { Growthbook } from 'components/Growthbook';
import { HeaderParent } from 'components/HeaderParent/HeaderParent';
import { IdMonitorContainer } from 'components/idMonitor/IdMonitorContainer';
import { OneTimePasscodeProvider } from 'components/idMonitor/Oxford/OneTimePasscodeContext';
import { LibContextWrapper } from 'components/LibContextWrapper';
import QueryWrapper from 'components/queryWrapper/QueryWrapper';
import { Recycling } from 'components/recycling/Recycling';
import { UpgradeLimitModalGate } from 'components/UpgradeLimitModalGate';
import {
  DashboardParent,
  Login,
  Report,
  RecordPage,
  AuthenticatedRoute,
  UnclaimedMoney,
  DarkWebScan,
  Billing,
  Lists,
  ListsItem,
  SearchPersonResultsContainerv2,
  SearchListingResults,
  ResetPassword,
  Reactivation,
  PublicRoute,
  NotFound,
  ErrorPage,
  UpgradePlanPage,
  ReportVelocityCaptcha,
  Monitoring,
  NeighborhoodReportSection,
  RewardsPage,
  MyGarage,
  IdMonitor,
  CreditBureauContacts,
  AlertDetails,
  SearchObituaryResults,
  Referrals,
  FamilyTreePage,
  PersonalityTraitsPage,
  Fraud,
  AstrologyPage,
  RecentReports,
  DashboardPage,
  NameHistorySearch,
  CourtCasesParent,
  CourtOpinionDetails,
  CourtCaseDetails,
  OralArgumentDetailsRoute,
  JudgeDetails,
  FeatureGatedRoute,
  IdentityHub,
  Notifications,
  MemberProfile,
  ReportAttachments,
} from 'routes';
import { DigitalFootprint } from 'routes/Dashboard/DigitalFootprint/DigitalFootprint';
import { OwnerSearchParent } from 'routes/Dashboard/OwnerSearch/OwnerSearchParent';
import { generateFooterLinks } from 'utils/footerLinks/footerLinks';
import { goToLogin } from 'utils/goToLogin';

import packageInfo from '../package.json';
// END IMPORTS

// START INTERFACES_AND_TYPES
declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    env: Record<string, string>;
  }
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
let otel: any = null;

if (
  import.meta.env.VITE_TARGET_ENV === 'production' ||
  import.meta.env.VITE_TARGET_ENV === 'staging'
) {
  otel = initialize({
    name: 'refresh-beenverified',
    url: import.meta.env.VITE_APM_URL,
    apikey: import.meta.env.VITE_APM_KEY,
  });
}
// END CONSTANTS

// START STYLED_COMPONENTS
const HeaderContainer = styled('div')`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 20;
`;

const MigrationBannerContainer = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const LibVersion = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 0 0 0`,
  fontSize: theme.typography.fontSize * 0.75,
}));
// END STYLED_COMPONENTS

// START HELPER_FUNCTIONS
function removeSensitiveLocalStorageData() {
  localStorage.removeItem('astrology_user');
  localStorage.removeItem(BaseNumerologyQueryKeys.lifepath);
  localStorage.removeItem(BaseNumerologyQueryKeys.personality);
}

function removeFamilyTreeLocalStorageData() {
  localStorage.removeItem('nodeRemovalApproved');
  localStorage.removeItem('reportIdsToFilterByType');
  localStorage.removeItem('bulkAddSubject');
  localStorage.removeItem('bulkAddSubjectReportInTree');
  localStorage.removeItem('bulkAddSubjectFamily');
  localStorage.removeItem('familyTreeRelativeToAdd');
  localStorage.removeItem('skipAutoTreeBuilder');
}
// END HELPER_FUNCTIONS

function App() {
  // START HOOKS_AND_QUERIES
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const [isCoreLoginRedirect] = useQueryParam(
    'is_core_login_redirect',
    StringParam
  );
  const [loginType] = useQueryParam('login_type', StringParam);
  const loginPageContainerRef = useRef(null);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isAuthenticated, setIsAuthenticated] = useState(isUserLoggedIn());
  const [isContactOptionsModalOpen, setIsContactOptionsModalOpen] =
    useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);
  const [isCancellationReasonsModalOpen, setIsCancellationReasonsModalOpen] =
    useState(false);
  const [, setIsDownsellOfferModalOpen] = useState(false);
  const [isUserExperienceSurveyModalOpen, setIsUserExperienceSurveyModalOpen] =
    useState(false);
  const [isTrustPilotModalOpen, setIsTrustPilotModalOpen] = useState(false);
  const [isInSingleReportVariation, setIsInSingleReportVariation] =
    useState(false);
  const [isInInternationalLanguageTest, setIsInInternationalLanguageTest] =
    useState(false);
  const [isInInternationalVinTest, setIsInInternationalVinTest] =
    useState(false);
  const [, setIsInListV2Variation] = useState(false);
  const [
    isInReactivationRedesignVariation,
    setIsInReactivationRedesignVariation,
  ] = useState(false);
  const [showCheaperPlanLink, setShowCheaperPlanLink] = useState(false);
  const [tosState, setTosState] = useState<{
    modalOpen: boolean;
    requestData: ErrorRequestData['body'] | null;
  }>({
    modalOpen: false,
    requestData: null,
  });
  const [isInOwnerSearchVariation, setIsInOwnerSearchVariation] =
    useState(false);
  const [
    isInAstrologyStandaloneVariation,
    setIsInAstrologyStandaloneVariation,
  ] = useState(false);
  const [isInCourtCasesVariation, setIsInCourtCasesVariation] = useState(false);
  const [oar1946Variation, setOar1946Variation] = useState('');
  const [, setIsSuccessModaVisible] = useState<boolean>(false);
  const [isInChatBVFlowFooterVariation, setIsInChatBVFlowFooterVariation] =
    useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const analytics = Analytics({
    app: 'refresh-beenverified',
    debug: true,
    plugins: [
      googleTagManager({
        containerId: 'GTM-HV76',
        dataLayer: (window as any)?.dataLayer ?? [],
      } as GoogleTagManagerConfig),
    ],
  });
  const storage = new CookiesStorage();
  const isChatBVRoute = location.pathname.includes('chatbv');
  const showFooter =
    !location.pathname.includes('self-searching') &&
    (!isChatBVRoute || isInChatBVFlowFooterVariation);
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (
      isCoreLoginRedirect === 'true' &&
      !sessionStorage.getItem('has_tracked_core_login_redirect')
    ) {
      analytics.track('site_event', {
        event_name: 'login_attempt',
        type: loginType || 'web',
        success: true,
      });

      if (loginType?.includes('sso')) {
        analytics.track('site_event', {
          event_name: 'sso_success',
          type: true,
        });
      }

      sessionStorage.setItem(
        'has_tracked_core_login_redirect',
        isCoreLoginRedirect
      );
    }
  }, [isCoreLoginRedirect]);
  // END EFFECTS

  // START EVENT_HANDLERS
  const changeIsInInternationalLanguageTest = (variation: boolean) => {
    setIsInInternationalLanguageTest(variation);
  };

  const changeIsInInternationalVinTest = (variation: boolean) => {
    setIsInInternationalVinTest(variation);
  };

  const changeIsInSingleReportVariation = (variation: boolean) => {
    setIsInSingleReportVariation(variation);
  };

  const changeIsInOwnerSearchVariation = (variation: boolean) => {
    setIsInOwnerSearchVariation(variation);
  };

  const changeIsInAstrologyStandaloneVariation = (variation: boolean) => {
    setIsInAstrologyStandaloneVariation(variation);
  };

  const changeIsInCourtCasesVariation = (variation: boolean) => {
    setIsInCourtCasesVariation(variation);
  };

  const changeOar1946Variation = (variation: string) => {
    setOar1946Variation(variation);
  };

  const changeIsInListV2Variation = (variation: boolean) => {
    setIsInListV2Variation(variation);
  };

  const changeIsInReactivationRedesignVariation = (variation: boolean) => {
    setIsInReactivationRedesignVariation(variation);
  };

  const changeIsInChatBVFlowFooterVariation = (variation: boolean) => {
    setIsInChatBVFlowFooterVariation(variation);
  };

  async function handleLogoutClick(ga4Ttracker?: () => void) {
    await invalidateSession();
    setIsAuthenticated(isUserLoggedIn());
    closeSnackbar();
    removeSensitiveLocalStorageData();
    removeFamilyTreeLocalStorageData();
    storage.setValue('partialLocView', 'false');
    localStorage.removeItem('upgradeLimitModalShown');
    goToLogin('https://www.beenverified.com');
    if (ga4Ttracker) {
      ga4Ttracker();
    }
  }

  function isResponseError(error: any): error is ResponseError {
    return error.response !== undefined;
  }

  function logError(
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) {
    const isProdEnv = import.meta.env.VITE_TARGET_ENV === 'production';
    if (!isProdEnv || !otel) {
      return;
    }

    const { account } = session;
    const { api } = otel;
    if (api.isOTELInitialized()) {
      if (account) {
        api.setUser({
          id: account?.account.user_info.user_code,
          email: account?.account.user_info.email,
        });
      }
      let errorMsg = `${context}: ${error.message}, Cause: ${error.cause}`;
      if (isResponseError(error)) {
        errorMsg = `${errorMsg}, Status: ${
          error.response.status
        }, Data: ${JSON.stringify(error.response)}`;
      }
      if (url) {
        errorMsg += `, URL: ${url}`;
      }
      api.pushError(new Error(errorMsg));
    }
  }

  function trackEventGA4(eventToTrack: object, eventName?: string) {
    const targetEnv = import.meta.env.VITE_TARGET_ENV;
    if (targetEnv === 'dev') {
      return;
    }
    analytics?.track(eventName || 'site_event', eventToTrack);
  }

  function trackEvent(category: string, action: string, label?: string) {
    analytics.track('ua_event', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
    });
  }

  const onLoginSuccess = (
    isInInternationalLanguageTest: boolean,
    isInInternationalVinTest: boolean
  ) => {
    let next = '/';

    if (location.state && location.state.next) {
      next = location.state.next;
    }

    if (isInInternationalLanguageTest || isInInternationalVinTest) {
      isInInternationalVinTest
        ? navigate('/dashboard/reports')
        : navigate('/dashboard/language');
    } else {
      navigate(
        next === '/' ||
          (next.startsWith('/report') && !next.includes('permalink'))
          ? '/dashboard'
          : next
      );
    }

    setIsAuthenticated(isUserLoggedIn());
  };

  const contactUsHandler = () => {
    setIsContactOptionsModalOpen(true);
  };

  const handleSearchSuccess = ({
    reportType,
    searchParams,
    permalink,
  }: SearchData) => {
    const url = `/report/${reportType}?${searchParams}&permalink=${permalink}`;
    navigate(url);
  };

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const handleCancellationModalClose = (reason: string | undefined) => {
    setIsCancellationReasonsModalOpen(false);
    if (reason === 'Too expensive') {
      setIsDownsellOfferModalOpen(true);
    } else if (reason === 'Other' || reason === 'No longer needed') {
      setIsUserExperienceSurveyModalOpen(true);
    }
  };

  const handleCloseUserExperienceSurveyModal = (
    rating: string | null | undefined
  ) => {
    setIsUserExperienceSurveyModalOpen(false);
    if (rating && parseInt(rating) >= 8) {
      setIsTrustPilotModalOpen(true);
    }
  };

  const openConfirmCancelModal = (downsellTest?: boolean) => {
    setIsConfirmCancelModalOpen(true);
    setShowCheaperPlanLink(downsellTest || false);
  };

  const onCancelConfirmCancelModal = () => {
    setIsConfirmCancelModalOpen(false);
    setIsCancellationReasonsModalOpen(true);
  };
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <ErrorBoundary
      redirectFunction={() => {
        navigate('/error');
      }}
      location={location}
      logFunction={logError as any}
      trackEventFunction={trackEventGA4}
    >
      <QueryWrapper
        handleLogout={handleLogoutClick}
        trackEventGA4={trackEventGA4}
        setTosState={setTosState}
      >
        <SessionProvider isAuthenticated={isAuthenticated}>
          <SubscriptionAccessConfigProvider>
            <Growthbook>
              <LibContextWrapper
                trackEventGA4={trackEventGA4}
                logError={logError}
                trackEvent={trackEvent}
                isInInternationalLanguageTest={isInInternationalLanguageTest}
                isInInternationalVinTest={isInInternationalVinTest}
                isInOwnerSearchVariation={isInOwnerSearchVariation}
                isInSingleReportVariation={isInSingleReportVariation}
                isInAstrologyStandaloneVariation={
                  isInAstrologyStandaloneVariation
                }
                isInCourtCasesVariation={isInCourtCasesVariation}
                handleLogoutClick={handleLogoutClick}
              >
                <EmailVerification>
                  <SearchFormProvider>
                    <div className="app">
                      <HeaderContainer>
                        <MigrationBannerContainer>
                          <BannerMigrationCta />
                        </MigrationBannerContainer>
                        <HeaderParent
                          handleSearchSuccess={handleSearchSuccess}
                          changeSingleReportVariation={
                            changeIsInSingleReportVariation
                          }
                          changeOwnerSearchVariation={
                            changeIsInOwnerSearchVariation
                          }
                          changeInternationalLanguageTest={
                            changeIsInInternationalLanguageTest
                          }
                          changeInternationalVinTest={
                            changeIsInInternationalVinTest
                          }
                          changeIsInAstrologyStandaloneVariation={
                            changeIsInAstrologyStandaloneVariation
                          }
                          changeCourtCasesVariation={
                            changeIsInCourtCasesVariation
                          }
                          changeListV2Variation={changeIsInListV2Variation}
                          changeOar1946Variation={changeOar1946Variation}
                          changeChatBVFlowFooterVariation={
                            changeIsInChatBVFlowFooterVariation
                          }
                        />
                      </HeaderContainer>
                      <AppContainer>
                        <Routes>
                          <Route element={<NotFound />} path={'*'} />
                          <Route element={<ErrorPage />} path={'/error'} />
                          <Route
                            index
                            element={<Navigate to={`/dashboard`} />}
                          />
                          <Route
                            path={'/family-tree'}
                            element={
                              <AuthenticatedRoute>
                                <FamilyTreePage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/self-searching`}
                            element={
                              <AuthenticatedRoute>
                                <SelfSearchingContainer />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/login`}
                            element={
                              <PublicRoute>
                                <Login
                                  onLoginSuccess={() =>
                                    onLoginSuccess(
                                      isInInternationalLanguageTest,
                                      isInInternationalVinTest
                                    )
                                  }
                                  ref={loginPageContainerRef}
                                />
                              </PublicRoute>
                            }
                          >
                            <Route
                              path="reset-password-request"
                              element={
                                <ResetPasswordRequestForm
                                  containerRef={loginPageContainerRef}
                                  open={false}
                                />
                              }
                            />
                            <Route
                              path="magic-link"
                              element={
                                <MagicLinkModal
                                  containerRef={loginPageContainerRef}
                                  open={false}
                                />
                              }
                            />
                            <Route
                              path="forgot-email"
                              element={
                                <ForgotLoginModal
                                  containerRef={loginPageContainerRef}
                                  open={false}
                                />
                              }
                            />
                          </Route>
                          <Route
                            path={`/reset-password`}
                            element={
                              <PublicRoute>
                                <ResetPassword />
                              </PublicRoute>
                            }
                          />
                          <Route
                            path={`dashboard`}
                            element={
                              <AuthenticatedRoute>
                                <DashboardParent
                                  contactUsHandler={contactUsHandler}
                                  handleLogout={handleLogoutClick}
                                  openConfirmCancelModal={
                                    openConfirmCancelModal
                                  }
                                />
                              </AuthenticatedRoute>
                            }
                          >
                            <Route
                              index
                              element={
                                <AuthenticatedRoute>
                                  <DashboardPage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/reports`}
                              element={
                                <AuthenticatedRoute>
                                  <RecentReports />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/personality-traits`}
                              element={
                                <AuthenticatedRoute>
                                  <PersonalityTraitsPage />
                                </AuthenticatedRoute>
                              }
                            />
                            {isInAstrologyStandaloneVariation && (
                              <Route
                                path={'/dashboard/astrology'}
                                element={
                                  <AuthenticatedRoute>
                                    <AstrologyPage />
                                  </AuthenticatedRoute>
                                }
                              />
                            )}

                            <Route
                              path={`/dashboard/lists/:listName`}
                              element={
                                <AuthenticatedRoute>
                                  <ListsItem />
                                </AuthenticatedRoute>
                              }
                            />

                            <Route
                              path={`/dashboard/family-insights`}
                              element={
                                <AuthenticatedRoute>
                                  <FamilyInsightsPage />
                                </AuthenticatedRoute>
                              }
                            />

                            <Route
                              path={`/dashboard/family-ancestry`}
                              element={
                                <AuthenticatedRoute>
                                  <FamilyAndAncestryDashboard />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/family-album`}
                              element={
                                <AuthenticatedRoute>
                                  <FamilyAlbum />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/ai-family-photos`}
                              element={
                                <AuthenticatedRoute>
                                  <AIFamilyPhotosPage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/rewards`}
                              element={
                                <AuthenticatedRoute>
                                  <RewardsPage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/dark-web`}
                              element={
                                <AuthenticatedRoute>
                                  <DarkWebScan />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/notifications`}
                              element={
                                <AuthenticatedRoute>
                                  <Notifications />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/lists`}
                              element={
                                <AuthenticatedRoute>
                                  <Lists />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/monitoring`}
                              element={
                                <AuthenticatedRoute>
                                  <Monitoring />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/identity-hub`}
                              element={
                                <AuthenticatedRoute>
                                  <IdentityHub />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/digital-footprint`}
                              element={
                                <AuthenticatedRoute>
                                  <DigitalFootprint />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route path={'/dashboard/id-monitor'}>
                              <Route
                                index
                                element={
                                  <AuthenticatedRoute>
                                    <OneTimePasscodeProvider>
                                      <IdMonitorContainer isOnIdMonitorPage />
                                    </OneTimePasscodeProvider>
                                  </AuthenticatedRoute>
                                }
                              />
                              <Route
                                path={'credit-bureau-contacts'}
                                element={
                                  <AuthenticatedRoute>
                                    <CreditBureauContacts />
                                  </AuthenticatedRoute>
                                }
                              />
                              <Route
                                path={'alert-details'}
                                element={
                                  <AuthenticatedRoute>
                                    <AlertDetails />
                                  </AuthenticatedRoute>
                                }
                              />
                            </Route>
                            <Route path={'/dashboard/personal-reports'}>
                              <Route
                                index
                                element={
                                  <AuthenticatedRoute>
                                    <IdMonitor />
                                  </AuthenticatedRoute>
                                }
                              />
                              <Route
                                path={'credit-bureau-contacts'}
                                element={
                                  <AuthenticatedRoute>
                                    <CreditBureauContacts />
                                  </AuthenticatedRoute>
                                }
                              />
                              <Route
                                path={'alert-details'}
                                element={
                                  <AuthenticatedRoute>
                                    <AlertDetails />
                                  </AuthenticatedRoute>
                                }
                              />
                            </Route>
                            <Route
                              path={'/dashboard/my-garage'}
                              element={
                                <AuthenticatedRoute>
                                  <MyGarage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={'/dashboard/obituary'}
                              element={
                                <AuthenticatedRoute>
                                  <ObituarySearchPage />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={'/dashboard/name-history'}
                              element={
                                <AuthenticatedRoute>
                                  <NameHistorySearch />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={'/dashboard/report-attachments'}
                              element={
                                <AuthenticatedRoute>
                                  <ReportAttachments />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={'neighborhood-report'}
                              element={
                                <AuthenticatedRoute>
                                  <NeighborhoodReportSection />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={'unclaimed-money'}
                              element={
                                <AuthenticatedRoute>
                                  <UnclaimedMoney />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`/dashboard/email-search`}
                              element={
                                <AuthenticatedRoute>
                                  <FeatureGatedRoute featureKey={'email'}>
                                    <EmailSearchPage />
                                  </FeatureGatedRoute>
                                </AuthenticatedRoute>
                              }
                            />
                            {isInCourtCasesVariation && (
                              <Route path={'court-cases'}>
                                <Route
                                  index
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtCasesParent />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'cases'}
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtCasesParent />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'opinions'}
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtCasesParent />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'oral-arguments'}
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtCasesParent />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'judge-search'}
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtCasesParent />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'opinions/:id'}
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtOpinionDetails />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'cases/:id'}
                                  element={
                                    <AuthenticatedRoute>
                                      <CourtCaseDetails />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'oral-arguments/:id'}
                                  element={
                                    <AuthenticatedRoute>
                                      <OralArgumentDetailsRoute />
                                    </AuthenticatedRoute>
                                  }
                                />
                                <Route
                                  path={'people/:id'}
                                  element={
                                    <AuthenticatedRoute>
                                      <JudgeDetails />
                                    </AuthenticatedRoute>
                                  }
                                />
                              </Route>
                            )}
                            <Route
                              path={`billing`}
                              element={
                                <AuthenticatedRoute>
                                  <Billing
                                    onCancelConfirmCancelModal={
                                      onCancelConfirmCancelModal
                                    }
                                  />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path={`referrals`}
                              element={
                                <AuthenticatedRoute>
                                  <Referrals />
                                </AuthenticatedRoute>
                              }
                            />
                            <Route
                              path="fraud"
                              element={
                                <AuthenticatedRoute>
                                  <Fraud
                                    onSearchSuccess={handleSearchSuccess}
                                  />
                                </AuthenticatedRoute>
                              }
                            />
                          </Route>
                          <Route
                            path={`/member-profile/:member_id`}
                            element={
                              <AuthenticatedRoute>
                                <MemberProfile />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/collection/:collection_id/record/:record_id`}
                            element={
                              <AuthenticatedRoute>
                                <RecordPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/family-map`}
                            element={
                              <AuthenticatedRoute>
                                <FamilyMapPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/report/:reportType`}
                            element={
                              <AuthenticatedRoute>
                                <FeatureGatedRoute reportTypeToFeature={true}>
                                  <Report
                                    onSearchSuccess={handleSearchSuccess}
                                  />
                                </FeatureGatedRoute>
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={'/chatbv/*'}
                            element={
                              <AuthenticatedRoute>
                                <ChatBVContainer
                                  footerProps={{
                                    footerLinks: generateFooterLinks({
                                      contactUsHandler,
                                      isInternationalTestUser:
                                        isInInternationalLanguageTest ||
                                        isInInternationalVinTest,
                                      oar1946Variation,
                                    }),
                                    libVersion: <></>,
                                    hasMobileApps: true,
                                    userInReactivationRedesignTest:
                                      isInReactivationRedesignVariation,
                                  }}
                                  onCancelConfirmCancelModal={
                                    onCancelConfirmCancelModal
                                  }
                                  openConfirmCancelModal={
                                    openConfirmCancelModal
                                  }
                                  contactUsHandler={contactUsHandler}
                                  onLogout={handleLogoutClick}
                                />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/person`}
                            element={
                              <AuthenticatedRoute>
                                <FeatureGatedRoute featureKey={'person'}>
                                  <SearchPersonResultsContainerv2 />
                                </FeatureGatedRoute>
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/v2`}
                            element={
                              <AuthenticatedRoute>
                                <SearchPersonResultsContainerv2 />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/listing`}
                            element={
                              <AuthenticatedRoute>
                                <FeatureGatedRoute featureKey={'property'}>
                                  <SearchListingResults />
                                </FeatureGatedRoute>
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/obituary`}
                            element={
                              <AuthenticatedRoute>
                                <FeatureGatedRoute featureKey={'obituary'}>
                                  <SearchObituaryResults />
                                </FeatureGatedRoute>
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/search/contact`}
                            element={
                              <AuthenticatedRoute>
                                <FeatureGatedRoute featureKey={'contact'}>
                                  <SearchPersonResultsContainerv2 />
                                </FeatureGatedRoute>
                              </AuthenticatedRoute>
                            }
                          />
                          {isInOwnerSearchVariation && (
                            <Route
                              path={`/search/owner`}
                              element={
                                <AuthenticatedRoute>
                                  <OwnerSearchParent />
                                </AuthenticatedRoute>
                              }
                            />
                          )}
                          <Route
                            path={`/noncompliance`}
                            element={
                              <AuthenticatedRoute>
                                <NonCompliancePage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-form`}
                            element={
                              <AuthenticatedRoute>
                                <ComplianceFormPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-permitted`}
                            element={
                              <AuthenticatedRoute>
                                <CompliancePermittedPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-prohibited`}
                            element={
                              <AuthenticatedRoute>
                                <ComplianceProhibitedPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={`/compliance-recomply`}
                            element={
                              <AuthenticatedRoute>
                                <ComplianceRecomplyPage />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={'/reactivation'}
                            element={
                              <AuthenticatedRoute>
                                <Reactivation
                                  changeIsInReactivationRedesignVariation={
                                    changeIsInReactivationRedesignVariation
                                  }
                                />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            index={true}
                            path={'/upgrade/plan'}
                            element={
                              <AuthenticatedRoute>
                                <UpgradePlanPage
                                  contactUsHandler={contactUsHandler}
                                />
                              </AuthenticatedRoute>
                            }
                          />
                          <Route
                            path={'/captcha'}
                            element={
                              <AuthenticatedRoute>
                                <ReportVelocityCaptcha />
                              </AuthenticatedRoute>
                            }
                          />
                        </Routes>
                        <Recycling isAuthenticated={isAuthenticated} />
                      </AppContainer>
                      <ContactOptionsModal
                        isOpen={isContactOptionsModalOpen}
                        setIsOpen={setIsContactOptionsModalOpen}
                        openConfirmCancelModal={openConfirmCancelModal}
                        plansWithout5ReportBlocker={
                          constants.data.plansWithout5ReportBlocker
                        }
                        validReportCountThreshold={
                          constants.data.validReportCountThreshold
                        }
                        blockUpgradeWindowThreshold={
                          constants.data.blockUpgradeWindowThreshold
                        }
                      />
                      <ConfirmCancelModal
                        isOpen={isConfirmCancelModalOpen}
                        onClose={() => setIsConfirmCancelModalOpen(false)}
                        onCancel={() => {
                          setIsConfirmCancelModalOpen(false);
                          setIsCancellationReasonsModalOpen(true);
                        }}
                        openSuccessModal={openSuccessModal}
                        showDownsell={true}
                        showCheaperPlanLink={showCheaperPlanLink}
                      />
                      <CancellationReasonsModal
                        isOpen={isCancellationReasonsModalOpen}
                        onClose={(reason) => {
                          handleCancellationModalClose(reason);
                        }}
                      />
                      <TosModalLegalError
                        tosState={tosState}
                        setTosState={setTosState}
                        handleSearchSuccess={handleSearchSuccess}
                      />
                      {isUserExperienceSurveyModalOpen && (
                        <CancelUserExperienceModal
                          onClose={(rating) =>
                            handleCloseUserExperienceSurveyModal(rating)
                          }
                        />
                      )}
                      <TrustPilotModal
                        isOpen={isTrustPilotModalOpen}
                        onClose={() => setIsTrustPilotModalOpen(false)}
                      />
                      <UpgradeLimitModalGate />
                      {showFooter && (
                        <Footer
                          footerLinks={generateFooterLinks({
                            contactUsHandler,
                            isInternationalTestUser:
                              isInInternationalLanguageTest ||
                              isInInternationalVinTest,
                            oar1946Variation,
                          })}
                          libVersion={
                            <LibVersion>
                              {import.meta.env.VITE_TARGET_ENV ===
                              'production' ? (
                                <code>{version}</code>
                              ) : (
                                <code>
                                  {
                                    packageInfo.dependencies[
                                      '@ltvco/refresh-lib'
                                    ]
                                  }
                                </code>
                              )}
                            </LibVersion>
                          }
                          hasMobileApps
                          openConfirmCancelModal={openConfirmCancelModal}
                          userInReactivationRedesignTest={
                            isInReactivationRedesignVariation
                          }
                        />
                      )}
                    </div>
                  </SearchFormProvider>
                </EmailVerification>
              </LibContextWrapper>
            </Growthbook>
          </SubscriptionAccessConfigProvider>
        </SessionProvider>
      </QueryWrapper>
    </ErrorBoundary>
  );
  // END RENDER
}

export default App;
